import React, { useState, useEffect, useMemo } from 'react'
import {
  ChevronLeft,
  Tag,
  CheckCircle,
  Circle,
  Search,
  RefreshCw,
  Copy
} from 'lucide-react'
import { categoryFieldAPI } from '../../services/api'

const CategoryFieldsSidebar = ({ isOpen, onClose, template_content }) => {
  const [categoryFields, setCategoryFields] = useState([])
  const [categoryInfo, setCategoryInfo] = useState(null)
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const categoryId = template_content?.subcategory_id || template_content?.category_id || null

  useEffect(() => {
    if (!isOpen) return
    fetchCategoryData()
  }, [isOpen, categoryId])

  const fetchCategoryData = async () => {
    if (!categoryId) {
      setCategoryFields([])
      setCategoryInfo(null)
      return
    }

    setLoading(true)
    try {
      const [fieldsRes, infoRes] = await Promise.all([
        categoryFieldAPI.listForCategory(categoryId),
        import('../../services/api').then(({ categoryAPI }) => categoryAPI.getById(categoryId))
      ])

      setCategoryFields(fieldsRes?.data?.data || [])
      if (infoRes?.data?.success) {
        setCategoryInfo(infoRes.data.data)
      }
    } catch (error) {
      console.error('Error loading category fields:', error)
      setCategoryFields([])
      setCategoryInfo(null)
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    if (!categoryId) return
    setRefreshing(true)
    try {
      const response = await categoryFieldAPI.listForCategory(categoryId)
      setCategoryFields(response?.data?.data || [])
    } catch (error) {
      console.error('Error refreshing category fields:', error)
    } finally {
      setRefreshing(false)
    }
  }

  const attachmentStats = useMemo(() => {
    const stats = {}

    const register = (fieldId) => {
      if (!fieldId) return
      const key = String(fieldId)
      stats[key] = (stats[key] || 0) + 1
    }

    const visitItem = (item) => {
      if (!item) return
      register(item.categoryFieldId || item.categoryField?.id)

      if (item.isPowerClipContainer && Array.isArray(item.powerClipContents)) {
        item.powerClipContents.forEach(visitItem)
      }
    }

    (template_content?.pages || []).forEach(page => {
      (page.items || []).forEach(visitItem)
    })

    return stats
  }, [template_content])

  const filteredFields = (categoryFields || []).filter(field => {
    if (!searchTerm) return true
    const value = (field.label || '') + (field.key || '')
    return value.toLowerCase().includes(searchTerm.toLowerCase())
  })

  const handleCopyPlaceholder = async (field) => {
    if (!navigator?.clipboard) return
    const placeholder = `{${field.key}}`

    try {
      await navigator.clipboard.writeText(placeholder)
    } catch (error) {
      console.warn('Failed to copy field placeholder', error)
    }
  }

  if (!isOpen) return null

  return (
    <aside className="fixed left-16 top-0 h-full w-80 bg-white border-r border-gray-200 shadow-lg z-[500] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900">Category Fields</h2>
        </div>
        <Tag className="w-5 h-5 text-gray-500" />
      </div>

      {/* Search + controls */}
      <div className="p-4 border-b border-gray-200 space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search fields..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        <button
          onClick={handleRefresh}
          disabled={!categoryId || refreshing}
          className="w-full flex items-center justify-center gap-2 rounded-lg border border-gray-200 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh fields
        </button>

        {categoryInfo && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            <div className="flex items-center gap-2 font-medium">
              <Tag className="w-4 h-4" />
              {categoryInfo.name}
            </div>
            {categoryInfo.description && (
              <p className="text-xs mt-1 text-blue-700">{categoryInfo.description}</p>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3" />
            Loading fields...
          </div>
        ) : filteredFields.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 px-6">
            <Tag className="w-12 h-12 text-gray-300 mb-3" />
            {categoryId ? (
              <>
                <p className="font-medium">No fields found</p>
                <p className="text-sm mt-1">Try a different search term or refresh.</p>
              </>
            ) : (
              <>
                <p className="font-medium">No category assigned</p>
                <p className="text-sm mt-1">Assign a category to this template to see its fields.</p>
              </>
            )}
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {filteredFields.map((field) => {
              const fieldId = String(field.id || field.field_id)
              const usageCount = attachmentStats[fieldId] || 0
              const isAttached = usageCount > 0

              return (
                <div
                  key={field.id}
                  onClick={() => handleCopyPlaceholder(field)}
                  className={`border rounded-lg p-3 cursor-pointer transition ${
                    isAttached
                      ? 'border-green-300 bg-green-50 hover:bg-green-100'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                  title="Click to copy placeholder"
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-semibold text-gray-900">{field.label}</div>
                    {isAttached ? (
                      <div className="flex items-center gap-1 text-xs text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <span className="px-2 py-0.5 rounded-full border border-green-200 bg-green-100">
                          Attached{usageCount > 1 ? ` ×${usageCount}` : ''}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Circle className="w-4 h-4" />
                        <span className="px-2 py-0.5 rounded-full border border-gray-200 bg-gray-100">
                          Available
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <Copy className="w-3 h-3" />
                    <code className="bg-gray-100 px-1 rounded">{`{${field.key}}`}</code>
                  </div>

                  <div className="mt-2 flex items-center gap-2 text-xs">
                    <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">
                      {field.field_type || field.fieldType}
                    </span>
                    {field.description && (
                      <span className="text-gray-500 truncate">{field.description}</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 p-3 text-xs text-gray-500 flex items-center justify-between">
        <div className="flex items-center gap-1 text-green-600">
          <CheckCircle className="w-3 h-3" />
          Attached
        </div>
        <div className="flex items-center gap-1 text-gray-500">
          <Circle className="w-3 h-3" />
          Available
        </div>
      </div>
    </aside>
  )
}

export default CategoryFieldsSidebar
